<template>
  <section class="features-section">
    <div class="features-container">
      <div class="section-header">
        <h2 class="section-title">
          <span class="section-title-icon">🚀</span>
          <span class="section-title-text">{{ title }}</span>
        </h2>
        <p class="section-subtitle">{{ subtitle }}</p>
      </div>
      <div class="features-grid">
        <div
          v-for="feature in features"
          :key="feature.id"
          class="feature-card"
          @click="handleFeatureClick(feature)"
          :class="{ 'clickable': clickable }"
        >
          <div class="feature-icon">{{ feature.icon }}</div>
          <h3 class="feature-title">{{ feature.title }}</h3>
          <p class="feature-description">{{ feature.description }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FeaturesSection',
  props: {
    title: {
      type: String,
      default: '核心功能特色'
    },
    subtitle: {
      type: String,
      default: '全面覆盖建筑工程技术专业教学需求，助力智能化学习'
    },
    features: {
      type: Array,
      default: () => [
        {
          id: 1,
          icon: '🤔',
          title: '苏格拉底式问答',
          description: '通过3轮以上连续引导式提问，培养学生批判性思维和深度理解能力'
        },
        {
          id: 2,
          icon: '🌏',
          title: '多语言实时辅导',
          description: '支持中文、English双语切换，实时翻译，满足国际化教学需求'
        },
        {
          id: 3,
          icon: '🎤',
          title: '多模态交互',
          description: '支持语音提问、手写拍照识别、文字输入等多种答疑方式'
        },
        {
          id: 4,
          icon: '📝',
          title: '智能题目生成',
          description: '根据课程进度针对性生成不同难度的课后练习题并提供辅导'
        },
        {
          id: 5,
          icon: '📱',
          title: '移动端优化',
          description: '完美适配移动设备，4G网络下流畅使用，随时随地学习'
        },
        {
          id: 6,
          icon: '🌐',
          title: '多终端支持',
          description: '支持Chrome/Firefox/Safari/Edge等主流浏览器，跨平台兼容'
        }
      ]
    },
    clickable: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: 'white'
    }
  },
  mounted() {
    this.initAnimation()
  },
  methods: {
    handleFeatureClick(feature) {
      if (this.clickable) {
        this.$emit('feature-click', feature)
      }
    },
    initAnimation() {
      // 使用 Intersection Observer 实现滚动动画
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-in')
            }
          })
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px'
        }
      )

      this.$nextTick(() => {
        const cards = this.$el.querySelectorAll('.feature-card')
        cards.forEach((card, index) => {
          card.style.opacity = '0'
          card.style.transform = 'translateY(40px)'
          card.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`
          observer.observe(card)
        })
      })
    }
  }
}
</script>

<style scoped>
/* CSS Variables */
.features-section {
  --primary: #2E5CE6;
  --primary-dark: #1E3A8A;
  --primary-light: #60A5FA;
  --secondary: #10B981;
  --accent: #06B6D4;
  --asean: #D97706;
  --china-red: #DC2626;
  --bg-white: #FFFFFF;
  --bg-gray-50: #F9FAFB;
  --bg-gray-100: #F3F4F6;
  --bg-gray-200: #E5E7EB;
  --text-primary: #111827;
  --text-secondary: #6B7280;
  --text-tertiary: #9CA3AF;
  --border-light: #E5E7EB;
  --border-medium: #D1D5DB;
  --shadow-xs: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-sm: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.features-section {
  background: v-bind(backgroundColor);
  padding: 80px 0;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.features-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-header {
  margin-bottom: 48px;
  text-align: center;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.5px;
}

.section-title-icon {
  font-size: 24px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.section-title-text {
  line-height: 1.2;
  display: inline-flex;
  align-items: center;
}

.section-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-weight: 400;
  opacity: 0.8;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 48px;
}

.feature-card {
  background: var(--bg-gray-50);
  border-radius: var(--radius-xl);
  padding: 32px;
  transition: var(--transition-base);
  border: 1px solid var(--border-light);
  position: relative;
  overflow: hidden;
}

.feature-card.clickable {
  cursor: pointer;
}

.feature-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, var(--primary) 0%, var(--asean) 100%);
  border-radius: var(--radius-xl);
  opacity: 0;
  transition: var(--transition-base);
  z-index: -1;
}

.feature-card:hover {
  transform: translateY(-4px);
  background: white;
  box-shadow: var(--shadow-xl);
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-card.animate-in {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

.feature-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-md);
  position: relative;
  z-index: 1;
}

.feature-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
  position: relative;
  z-index: 1;
}

.feature-description {
  font-size: 16px;
  color: var(--text-secondary);
  line-height: 1.6;
  position: relative;
  z-index: 1;
}

/* Responsive */
@media (max-width: 768px) {
  .features-section {
    padding: 60px 0;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .section-title {
    font-size: 28px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  .feature-card {
    padding: 24px;
  }

  .feature-icon {
    width: 56px;
    height: 56px;
    font-size: 28px;
  }

  .feature-title {
    font-size: 18px;
  }

  .feature-description {
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .features-section {
    padding: 40px 0;
  }

  .section-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }

  .features-grid {
    margin-top: 32px;
  }
}

/* Animation performance optimization */
@media (prefers-reduced-motion: reduce) {
  .feature-card {
    transition: none !important;
  }
}
</style>